/**
 * #1071 — Game Detail's own initial Spread/Total line when the board stored
 * none for the game (`docs/261007Issue1071HighestVolumeInitialLine.md`, "Game
 * Detail follows the board"). Switched on only. Same rule and timing as the
 * board: the 50¢ line shows unstored while the window's volume arrives, then
 * one line is seeded and kept; a touch first holds what is on screen.
 *
 * The seed is never a pick: it is not written to the #1078 reading view, so
 * Recent views and the missing-pick note still name only lines the trader
 * touched. It is given to the board's default map, so a later board visit opens
 * the game on the same line, and counted like a board seed
 * (`initialLineTally.ts`).
 */
import type { SpreadLine, TotalLine } from '../ValueDashboard/types';
import { initialLineStep, type InitialLineOutcome } from '../SpreadsTotals/initialLine';
import { INITIAL_LINE_WAIT_MS } from '../SpreadsTotals/initialLineConfig';
import { defaultLineIndex, nearestToFiftyIndex } from '../SpreadsTotals/lineDefault';

type Line = SpreadLine | TotalLine;

function listedIndex<T>(lines: readonly T[], keyFor: (line: T) => string, key: string | null): number {
  return key == null ? -1 : lines.findIndex((line) => keyFor(line) === key);
}

/** The line a strip with no line of its own shows: the line it settled on
 *  this visit (`settledKey`: the board's line it fell back to, or its own seed),
 *  else the board's stored line, else the rule's answer when it is already
 *  final (so storing it moves nothing), else the 50¢ line (`defaultLineIndex`).
 *  The settled line comes first, as the board keeps a row's existing key: a
 *  board line listed again later (a delisted board pick returning) does not
 *  move a strip that has settled (#823: a returning line does not reclaim a
 *  visible fallback). */
export function initialLineDisplayIndex<T extends Line>(
  lines: T[], keyFor: (line: T) => string, boardIdx: number | null, settledKey: string | null,
): number {
  const seeded = listedIndex(lines, keyFor, settledKey);
  if (seeded >= 0) return seeded;
  if (boardIdx != null) return boardIdx;
  const step = initialLineStep(lines, nearestToFiftyIndex(lines), null, INITIAL_LINE_WAIT_MS);
  return step.kind === 'seed' ? step.idx : defaultLineIndex(lines);
}

/** What one settle pass does for one strip.
 *  - `idle`: nothing to decide — the strip's settled line is listed, the
 *    trader holds a listed line (a pick, a touch, a focus link, a Recent
 *    return), or there is no 50¢ line yet (#780: no clock). A held line is
 *    neither saved nor counted: the hold stays on Game Detail, so the board
 *    settles that row itself on its next visit and counts it then. (A held
 *    line that is not listed yet, such as a late focus or Recent line, does
 *    not stop the rule, as on the board.)
 *  - `keep`: settle on the board's stored line, `key`; it is the board's own
 *    line, so Game Detail neither saves nor counts it.
 *  - `wait`: the window's volume is incomplete; start the wait if needed.
 *  - `seed`: settle on `key`, give it to the board and count it as `outcome`
 *    (`touched`: worked while waiting, see below). */
export type InitialLineSettle =
  | { kind: 'idle' }
  | { kind: 'keep'; key: string }
  | { kind: 'wait' }
  | { kind: 'seed'; key: string; outcome: InitialLineOutcome | { kind: 'touched' } };

export function settleInitialLine<T extends Line>(args: {
  lines: T[];
  keyFor: (line: T) => string;
  /** The trader holds a line (a pick, a touch, a focus link, a Recent return)
   *  that is listed: the rule is never consulted. */
  held: boolean;
  boardIdx: number | null;
  /** The line this strip settled on this visit, if any. */
  settledKey: string | null;
  /** Expanded, aimed, or the line the open ticket follows, right now. */
  worked: boolean;
  /** How long this strip has shown its 50¢ line waiting (null: not started). */
  waitedMs: number | null;
}): InitialLineSettle {
  const { lines, keyFor, held, boardIdx, settledKey, worked, waitedMs } = args;
  if (held || listedIndex(lines, keyFor, settledKey) >= 0) return { kind: 'idle' };
  if (boardIdx != null) return { kind: 'keep', key: keyFor(lines[boardIdx]) };
  const anchor = nearestToFiftyIndex(lines);
  const step = initialLineStep(lines, anchor, waitedMs, INITIAL_LINE_WAIT_MS);
  if (step.kind === 'unanchored') return { kind: 'idle' };
  if (step.kind === 'seed') return { kind: 'seed', key: keyFor(lines[step.idx]), outcome: step.outcome };
  // Worked but holding no listed line (its held line left the board) while the
  // volume is pending: keep the 50¢ line it shows, so the open card or ticket
  // does not change strike when the answer lands.
  if (worked && anchor != null) return { kind: 'seed', key: keyFor(lines[anchor]), outcome: { kind: 'touched' } };
  return { kind: 'wait' };
}
